/**
 * 模块名单常量 — 我的空间图片/视频 Tab 过滤用
 *
 * 双重职责：
 * 1. 请求参数 — 传给 versionListNew 接口的 moduleList 字段，服务端按模块过滤
 * 2. 子类型判断 — 客户端根据 module 落在哪个数组判断图片子类型（单图/组图/PSD）
 *
 */

import { ModuleId } from "@xmiles/holopix-types";
import { MODULE_ID_CANVAS_DRAFT_A3, MODULE_ID_EXPLORE_A3 } from "@/constants/a3ModuleIds";

/**
 * 单图模块名单 — 图片 Tab「单图」子 Tab 用
 */
export const SINGLE_IMAGE_MODULES: number[] = [
  //极速编辑 Lite
  ModuleId.KleinFast,
  //全能编辑 V1
  ModuleId.SeedDream,
  //全能编辑 V2
  ModuleId.Monkey,
  //全能编辑 V2.1
  ModuleId.MonkeyV21,
  //全能编辑 V3
  ModuleId.GptImage2,
  //全能编辑 V3.5 精绘
  ModuleId.GptImageV35,
  //全能编辑 V3.5 极速
  ModuleId.GptImageV35Flare,
  //全能编辑 V3 企业版
  ModuleId.GptImage2Enterprise,
  //自由画布-万能渲染
  ModuleId.CanvasRender,
  //自由画布-一键抠图
  ModuleId.CanvasMatting,
  //自由画布-智能扩图
  ModuleId.VisualExpansionV2,
  //自由画布-相似图裂变
  ModuleId.CanvasFission,
  //自由画布-一键成稿（含 A3）
  ModuleId.CanvasDraft,
  MODULE_ID_CANVAS_DRAFT_A3,
  //自由画布-重绘放大
  ModuleId.CanvasRepaintUpscale,
  //自由画布-自由视角
  ModuleId.CanvasQwenFreePerspective,
  //自由画布-局部修改
  ModuleId.CanvasLocalRefine,
  //Midjourney V7
  ModuleId.Midjourney,
  //Midjourney V8.1
  ModuleId.MidjourneyV81,
  //Midjourney Niji7
  ModuleId.MidjourneyNiji7,
  //Midjourney V8.2
  ModuleId.MidjourneyV82,
  // 阿里云悠船
  ModuleId.AliyunYouchuan,
  ModuleId.AliyunYouchuanV81,
  ModuleId.AliyunYouchuanNiji7,
  ModuleId.AliyunYouchuanV82Fastest,
  //自由画布-Seedream V5 Pro
  ModuleId.CanvasSeedreamV5Pro
];

/**
 * PSD 模块名单 — 图片 Tab「PSD」子 Tab 用
 */
export const PSD_MODULES: number[] = [
  //自由画布-智能模型（待确认）
  ModuleId.CanvasComfyUIPSD
];

/**
 * 组图模块名单 — 图片 Tab「组图」子 Tab 用
 * 注意：CanvasComfyUISingle(35) 属于智能模型名单，不在此列（module 35 记录的
 * 单图/组图/PSD/视频形态由 picsType 决定，统一归入「智能模型」子 Tab）
 */
export const GROUP_IMAGE_MODULES: number[] = [
  //自由画布-组图
  ModuleId.CanvasComfyUIMulti
];

/**
 * 智能模型模块名单 — 图片 Tab「智能模型」子 Tab 用
 * module 恒为 35（ComfyUI 模板），记录的真实产物类型由 param 中模板 picsType 决定
 */
export const COMFYUI_MODULES: number[] = [
  //自由画布-智能模型
  ModuleId.CanvasComfyUISingle
];

/**
 * 全能拆分模块名单 — 图片 Tab「全能拆分」子 Tab 用
 * 仅 87 = 全能拆分（GameUiSplit）；88 图层修补 / 89 Klein 图像修补的记录不归入该 Tab
 */
export const GAME_UI_SPLIT_MODULES: number[] = [
  ModuleId.GameUiSplit
];

/**
 * 视频模块名单 — 视频 Tab 用
 * 包含画布视频模块和 AI 创作视频模块
 */
export const VIDEO_MODULES: number[] = [
  //自由画布-首尾帧视频
  ModuleId.CanvasStartEndVideo,
  //自由画布-多模态视频
  ModuleId.CanvasReferenceVideo,
  //自由画布-图生视频
  ModuleId.CanvasImage2Video,
  //自由画布-首尾帧视频（seedance）
  ModuleId.SeedanceStartEndVideo,
  //自由画布-全能参考（seedance）
  ModuleId.SeedanceReferenceVideo,
  //自由画布-图生视频（seedance）
  ModuleId.SeedanceImage2Video,
  //自由画布-文生视频（seedance）
  ModuleId.SeedanceText2Video,
  //自由画布-首尾帧视频（seedance mini）
  ModuleId.SeedanceMiniStartEndVideo,
  //自由画布-全能参考（seedance mini）
  ModuleId.SeedanceMiniReferenceVideo,
  //自由画布-图生视频（seedance mini）
  ModuleId.SeedanceMiniImage2Video,
  //自由画布-文生视频（seedance mini）
  ModuleId.SeedanceMiniText2Video,
  //自由画布-MiniMax H3 视频（Runninghub，83-86）
  ModuleId.MinimaxH3StartEndVideo,
  ModuleId.MinimaxH3ReferenceVideo,
  ModuleId.MinimaxH3Image2Video,
  ModuleId.MinimaxH3Text2Video,
  ModuleId.MinimaxH3FastStartEndVideo,
  ModuleId.MinimaxH3FastReferenceVideo,
  ModuleId.MinimaxH3FastImage2Video,
  ModuleId.MinimaxH3FastText2Video,
  ModuleId.MinimaxH3MaxStartEndVideo,
  ModuleId.MinimaxH3MaxImage2Video,
  ModuleId.MinimaxH3MaxText2Video
];

/**
 * 3D 模块名单 — 3D 模型资产用
 *
 * NOTE: 当前仅声明分类，消费链路（3D Tab / 列表拉取）尚未接入，
 * 见 space/index.vue 中暂时隐藏的「3D模型」Tab。先在此登记 id=73，
 * 保证启动完整性断言通过；后续 3D 资产能力开发时再接入消费方。
 * 与 moduleActionWhitelist.ts 中的 THREE_D_MODULES（操作按钮白名单，id=11）职责不同，勿混用。
 */
export const THREE_D_MODULES: number[] = [
  //自由画布-3D生成（混元3D）
  ModuleId.CanvasGenerate3d
];

// 思考了一下,算了,不需要完整性校验了,手动维护好了
// // ============================================================
// // 完整性断言：防止新增画布模块时遗漏分类
// // ============================================================

// const _classifiedIds = new Set<number>([
//   ...SINGLE_IMAGE_MODULES,
//   ...GROUP_IMAGE_MODULES,
//   ...PSD_MODULES,
//   ...VIDEO_MODULES,
//   ...THREE_D_MODULES
// ]);

// const _unclassified = canvasModuleConfigurations.filter((m) => !_classifiedIds.has(m.id));

// if (_unclassified.length > 0) {
//   const details = _unclassified.map((m) => `  - id=${m.id} key=${m.key} label=${m.label}`).join("\n");
//   throw new Error(
//     `[moduleListConstants] 以下画布模块未分类到单图/组图/PSD/视频/3D白名单：\n${details}\n` +
//       `请在 moduleListConstants.ts 中补充对应分类。`
//   );
// }
